import { X } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from '@kostbar/i18n'
import { logger } from '../utils/logger'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type Option = { label: string; value: string }

type SelectFilterProps = {
  value?: string
  placeholder?: string
  options: Array<Option>
  onChange: (value: string | undefined) => void
}

const ALL = '__ALL__'

export function SelectFilter({
  value,
  placeholder,
  options,
  onChange,
}: SelectFilterProps) {
  const { t: tUi } = useTranslation('ui')
  const hasAllOption = options.some(
    (o) => o.value.toLowerCase() === 'all' || o.label.toLowerCase() === 'all',
  )
  const fallbackValue = hasAllOption
    ? (options.find((o) => o.value.toLowerCase() === 'all')?.value ?? 'all')
    : ALL
  const normalized = value ?? fallbackValue
  const hasValue = value !== undefined

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Clear filter on Escape key
    if (e.key === 'Escape' && hasValue) {
      e.preventDefault()
      onChange(undefined)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        value={normalized}
        onValueChange={(v) => {
          logger.debug('[SelectFilter] onValueChange', { prev: value, next: v })
          if (fallbackValue === ALL) {
            onChange(v === ALL ? undefined : v)
          } else {
            onChange(v)
          }
        }}
        onOpenChange={(open) => {
          logger.debug('[SelectFilter] onOpenChange', { open })
        }}
      >
        <SelectTrigger className="w-[220px]" onKeyDown={handleKeyDown}>
          <SelectValue placeholder={placeholder ?? tUi('filters.select')} />
        </SelectTrigger>
        <SelectContent>
          {fallbackValue === ALL && (
            <SelectItem value={ALL}>{tUi('filters.all')}</SelectItem>
          )}
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasValue ? (
        <button
          type="button"
          aria-label={tUi('filters.clearFilter')}
          className="text-muted-foreground hover:text-foreground"
          onClick={() => {
            logger.debug('[SelectFilter] clear clicked')
            onChange(undefined)
          }}
        >
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  )
}
